import { SchoolContentSectionType } from '@axl/accel-framework/models';
import Api from 'api';
import { action, computed, observable, runInAction } from 'mobx';
import { SchoolContentItemFavorite, SchoolContentItemFavoriteFilter } from 'models';
import SchoolStore from 'stores/SchoolStore/SchoolStore';
import UserStore from 'stores/UserStore/UserStore';

export default class FavoriteStore {
    constructor(private api: Api, private schoolStore: SchoolStore, private userStore: UserStore) {
    }

    @observable items: SchoolContentItemFavorite[] = [];
    @observable isLoading: boolean = false;
    @observable isLoaded: boolean = false;
    @observable loadedForStudentId: string | null = null;

    @computed get isEnabled() {
        return this.schoolStore.school?.favoritesEnabled == true;
    }

    @computed get icon() {
        return this.schoolStore.school?.favoritesIconOrDefault ?? '';
    }

    @computed get count() {
        return this.items.length;
    }

    @computed get itemsByItemId() {
        return new Map(this.items.map(x => [x.itemId, x]));
    }

    getByItemId(itemId: string) {
        return this.itemsByItemId.get(itemId);
    }

    isFavorite(itemId: string) {
        return this.getByItemId(itemId) != null;
    }

    @action async load(force: boolean = false) {
        if (!this.isEnabled || !this.userStore.isAuthorized) return;

        const studentId = this.userStore.currentUser.id;
        if (this.loadedForStudentId != studentId)
            this.reset();

        if (this.isLoaded && !force) return;
        if (this.isLoading) return;

        this.isLoading = true;
        const result = await this.api.favorite.fetch(new SchoolContentItemFavoriteFilter({ useBaseFilter: false }));
        runInAction(() => {
            if (result.success) {
                this.items = result.response.items ?? [];
                this.isLoaded = true;
                this.loadedForStudentId = studentId;
            }
            this.isLoading = false;
        });
    }

    @action async toggle(sectionType: SchoolContentSectionType, itemId: string) {
        const favorite = this.getByItemId(itemId);
        if (favorite != null)
            return await this.remove(favorite.id);

        const result = await this.api.favorite.create({
            sectionType,
            courseItemId: sectionType == SchoolContentSectionType.Course ? itemId : undefined,
            libraryItemId: sectionType == SchoolContentSectionType.Library ? itemId : undefined
        });
        runInAction(() => {
            if (result.success && result.response.body != null)
                this.items = [result.response.body, ...this.items];
        });
        return result;
    }

    @action async remove(id: string) {
        const result = await this.api.favorite.delete(id);
        runInAction(() => {
            if (result.success)
                this.items = this.items.filter(x => x.id != id);
        });
        return result;
    }

    @action reset() {
        this.items = [];
        this.isLoaded = false;
        this.loadedForStudentId = null;
    }
}
